import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Handshake } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABELS = {
  name: "Volledige naam",
  role_title: "Functie / rol",
  company: "Bedrijf",
  email: "E-mailadres",
  phone: "Telefoonnummer",
  relationship: "Relatie / hoe je elkaar kent",
  years_known: "Aantal jaren dat je elkaar kent",
  note: "Korte notitie / aanbeveling",
};

export default function ReferenceApprove() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const initialAction = params.get("action") || "";
  const [state, setState] = useState({ loading: true, error: "", data: null, done: "" });
  const [shareFields, setShareFields] = useState([]);
  const [extra, setExtra] = useState({ role_title: "", company: "", years_known: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(initialAction === "reject" ? "reject" : "approve");

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/refs/${token}`)
      .then((res) => {
        if (cancelled) return;
        setState({ loading: false, error: "", data: res.data, done: "" });
        setShareFields(res.data.default_share_fields || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ loading: false, error: err?.response?.data?.detail || "Uitnodiging niet gevonden", data: null, done: "" });
      });
    return () => { cancelled = true; };
  }, [token]);

  const toggle = (f) => setShareFields((prev) =>
    prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]);

  const submitApprove = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/refs/${token}/approve`, { share_fields: shareFields, ...extra });
      setState((s) => ({ ...s, done: "approved" }));
    } catch (e) {
      setState((s) => ({ ...s, error: e?.response?.data?.detail || "Kon niet opslaan" }));
    } finally {
      setBusy(false);
    }
  };

  const submitReject = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/refs/${token}/reject`);
      setState((s) => ({ ...s, done: "rejected" }));
    } catch (e) {
      setState((s) => ({ ...s, error: e?.response?.data?.detail || "Kon niet opslaan" }));
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <div className="ed-page min-h-screen flex items-center justify-center">
        <Loader2 size={22} className="animate-spin text-neutral-500" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="ed-page min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="ref-approve-error">
          <XCircle size={40} className="text-rose-600 mx-auto mb-4" strokeWidth={1.4} />
          <h1 className="font-serif text-2xl mb-2">Uitnodiging niet meer geldig</h1>
          <p className="text-sm text-neutral-600">{state.error}</p>
        </div>
      </div>
    );
  }

  if (state.done === "approved") {
    return (
      <div className="ed-page min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="ref-approve-thanks">
          <CheckCircle2 size={48} className="text-emerald-600 mx-auto mb-4" strokeWidth={1.4} />
          <h1 className="font-serif text-3xl mb-2">Dank voor je steun!</h1>
          <p className="text-neutral-600">
            Je akkoord is genoteerd. {state.data.requester_name} kan je nu opgeven als referentie
            voor sollicitaties. Werkgevers nemen alleen contact op wanneer {state.data.requester_name} jouw naam expliciet doorgeeft.
          </p>
        </div>
      </div>
    );
  }

  if (state.done === "rejected") {
    return (
      <div className="ed-page min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="ref-approve-rejected">
          <XCircle size={40} className="text-neutral-500 mx-auto mb-4" strokeWidth={1.4} />
          <h1 className="font-serif text-2xl mb-2">Bedankt voor de terugkoppeling</h1>
          <p className="text-neutral-600">
            We hebben {state.data.requester_name} laten weten dat je liever geen referentie bent op dit moment.
          </p>
        </div>
      </div>
    );
  }

  const d = state.data;
  return (
    <div className="ed-page min-h-screen py-14 px-6" data-testid="ref-approve-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-5">
          <Handshake size={22} strokeWidth={1.5} />
          <p className="ed-eyebrow">Verzoek als referentie</p>
        </div>
        <h1 className="font-serif text-4xl leading-tight mb-4">
          Hallo <span className="serif-italic">{d.reference_name.split(" ")[0] || "daar"}</span>,
        </h1>
        <p className="text-neutral-700 leading-relaxed mb-3">
          <strong>{d.requester_name}</strong> werkt aan een nieuwe stap in zijn loopbaan
          en zou jou graag opgeven als referentie. Daarom vragen we je om akkoord te geven.
        </p>
        <p className="text-neutral-700 leading-relaxed mb-8">
          Als je akkoord gaat, kun je zelf aangeven welke gegevens je wilt delen —
          bijvoorbeeld je naam, rol of telefoonnummer. Je hoeft nu niets te doen.
          Een werkgever kan contact opnemen, maar tegen die tijd hoor je dat altijd
          vooraf van {d.requester_name}, zodat je precies weet wat je kunt verwachten.
        </p>

        {mode === "approve" ? (
          <div className="ed-card p-6" data-testid="ref-approve-form">
            <p className="ed-label mb-3">Welke gegevens mogen wij delen?</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
              {d.shareable_fields.map((f) => (
                <label key={f} className="flex items-start gap-2 border border-neutral-200 px-3 py-2 cursor-pointer hover:bg-neutral-50">
                  <input
                    type="checkbox"
                    data-testid={`ref-share-${f}`}
                    checked={shareFields.includes(f)}
                    onChange={() => toggle(f)}
                    className="mt-0.5 w-4 h-4 accent-neutral-900"
                  />
                  <span className="text-sm">{FIELD_LABELS[f] || f}</span>
                </label>
              ))}
            </div>

            <p className="ed-label mb-3">Optioneel: extra context (alleen gedeeld als je het hierboven aanvinkt)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              <input data-testid="ref-input-role" className="ed-input" placeholder="Functie / rol" value={extra.role_title} onChange={(e) => setExtra({ ...extra, role_title: e.target.value })} />
              <input data-testid="ref-input-company" className="ed-input" placeholder="Bedrijf" value={extra.company} onChange={(e) => setExtra({ ...extra, company: e.target.value })} />
              <input data-testid="ref-input-years" className="ed-input" placeholder="Jaren dat je elkaar kent" value={extra.years_known} onChange={(e) => setExtra({ ...extra, years_known: e.target.value })} />
              <textarea data-testid="ref-input-note" className="ed-input min-h-[3.5rem]" placeholder="Korte notitie / aanbeveling" value={extra.note} onChange={(e) => setExtra({ ...extra, note: e.target.value })} />
            </div>

            <div className="flex flex-wrap gap-3">
              <button data-testid="ref-approve-submit" onClick={submitApprove} disabled={busy} className="ed-btn">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} strokeWidth={1.7} />}
                Ja, ik geef akkoord
              </button>
              <button data-testid="ref-approve-switch-reject" onClick={() => setMode("reject")} className="ed-btn ed-btn-outline">
                Liever niet
              </button>
            </div>
          </div>
        ) : (
          <div className="ed-card p-6" data-testid="ref-reject-form">
            <p className="text-neutral-700 mb-5">
              Weet je zeker dat je geen referentie wilt zijn voor {d.requester_name}? Geen probleem — we
              laten het gewoon weten.
            </p>
            <div className="flex flex-wrap gap-3">
              <button data-testid="ref-reject-submit" onClick={submitReject} disabled={busy} className="ed-btn">
                {busy ? <Loader2 size={13} className="animate-spin" /> : <XCircle size={13} strokeWidth={1.7} />}
                Ja, afwijzen
              </button>
              <button data-testid="ref-reject-switch-approve" onClick={() => setMode("approve")} className="ed-btn ed-btn-outline">
                Terug
              </button>
            </div>
          </div>
        )}

        <p className="text-xs text-neutral-500 mt-8 leading-relaxed">
          Deze link is eenmalig en persoonsgebonden. Je gegevens worden alleen gedeeld
          binnen JobGrid en niet automatisch met werkgevers.
        </p>
      </div>
    </div>
  );
}
